/*
 * screen-recorder/replay.js — «Мгновенный повтор» (instant replay).
 *
 * The recorder runs as usual, but no take is written: every encoded chunk goes
 * into a loop on disk that keeps the last N seconds, and «Сохранить повтор»
 * cuts the last 30 s / 1 / 2 / 5 min out of it as one file — the frames are
 * not re-encoded, so a clip costs a read of a few megabytes and no quality.
 * The game, the call, the stream run for hours; only the moments are kept.
 *
 * The loop is the dash camera's (public/js/dashcam/): LoopWriter writes the
 * chunks as records into segments that begin on keyframes, a storage worker
 * holds them with sync access handles in «sr-replay/» of the private file
 * system, the oldest segments are forgotten, and mux.js builds the clip —
 * MP4 for an H.264 take, WebM for a VP9/AV1 one (the take's own container).
 * The segment and clip names carry the page's OPFS owner tag
 * (window.__opfsOwner): when the page is gone, the next page load anywhere on
 * the site removes them (timbrica-core.js, kind `replay`).
 *
 *   const s = await SrReplay.open({ workerUrl, windowS, bytesPerSec })
 *     → session, or {refused:'no_room', needBytes, freeBytes}
 *   engine opts.replay = s.sink      // begin(shape) / video / audio / end
 *   s.haveUs()                       // how much the loop holds now (µs)
 *   await s.save(seconds)            // → {blob, ext, durUs, bytes, file}
 *   await s.discard(file)            // a clip copied elsewhere (the folder) — its file goes
 *   await s.close()                  // the loop removed (clips stay while the page lives)
 *
 * Guards: tests/js/dashcam-loop-writer.test.mjs, tests/js/loop-webm-clip.test.mjs,
 * tests/Unit/ScreenRecorderInstantReplayTest.php; stand —
 * tests/scratch/sr-replay-stand-scratch.mjs.
 */
import { openStore } from '../dashcam/store.js';
import { LoopWriter, videoTap, audioTap } from '../dashcam/loop-writer.js';
import { loopBytes, SEG_US } from '../dashcam/ring.js';
import { buildMp4, buildWebm, estimateBytes } from '../dashcam/mux.js';

const DIR = 'sr-replay';
const KEEP_CLIPS = 3;
let storeP = null;

function tag() {
    try { return (window.__opfsOwner && window.__opfsOwner.tag('-')) || ''; } catch (_) { return ''; }
}

/** The same storage worker for every session of the page (one per «sr-replay/»). */
function store(workerUrl) {
    if (!storeP) storeP = openStore({ workerUrl, dir: DIR }).catch((e) => { storeP = null; throw e; });
    return storeP;
}

/**
 * Room for a loop of `windowS` at `bytesPerSec` — measured through the site's
 * door (__storageEstimate: navigator.storage.estimate() answers «usage +
 * 10 GiB» on most Chromium builds whatever the disk holds).
 */
async function roomFor(need) {
    if (typeof window.__storageEstimate !== 'function') return { ok: true, free: null };
    try {
        const est = await window.__storageEstimate({ upTo: Math.ceil(need * 1.5) });
        if (!est || !est.quota) return { ok: true, free: null };
        const free = Math.max(0, est.quota - est.usage);
        return { ok: free >= need, free };
    } catch (_) { return { ok: true, free: null }; }
}

async function open({ workerUrl, windowS, bytesPerSec }) {
    const loopUs = Math.max(10, windowS) * 1e6;
    const bps = Math.max(32000, bytesPerSec || 0);
    // the loop plus one segment of slack, and the biggest clip built next to it
    const need = loopBytes(loopUs, bps * 8, 0) + Math.ceil(bps * (loopUs + SEG_US) / 1e6);
    const room = await roomFor(need);
    if (!room.ok) return { refused: 'no_room', needBytes: need, freeBytes: room.free };
    const st = await store(workerUrl);
    const stamp = Date.now();
    const own = tag();
    const files = new Set();
    let cfg = { video: null, audio: null }, shape = null, writer = null, clipN = 0, closed = false;
    let failure = null;
    const building = new Set();
    const clips = [];
    const name = (n) => { const f = 'r' + stamp + own + '-' + n + '.bin'; files.add(f); return f; };

    writer = new LoopWriter({
        store: st, name, loopUs,
        // the byte cap is the room the loop was promised: a busy screen at a high
        // rate shortens the loop instead of filling the disk
        maxBytes: Math.ceil(loopBytes(loopUs, bps * 8, 0) * 1.5),
        locks: () => [...building],
        onCreateError: (e) => refused(e),
    });
    // A refused write (the disk filled under the loop): cut back and go on with
    // a shorter loop; when not even 20 s fits, the session says so.
    function refused(e) {
        const quota = !!(e && (e.name === 'QuotaExceededError' || /quota|space|short write/i.test(String(e.message))));
        if (!writer || !writer.segmentOf(e)) return;
        if (!quota) { failure = 'store_error'; return; }
        writer.refused(e);
        const cap = Math.floor(writer.ix.bytes * 0.8);
        writer.shrink(cap, Math.max(20e6, Math.floor(cap / bps) * 1e6));
        if (cap < bps * 20) failure = 'store_full';
        tel('record_replay_disk_shrink', { mb: Math.round(writer.ix.bytes / 1048576), s: Math.round(writer.loopUs / 1e6) });
    }
    st.onError = (e) => refused(e);
    const flushTimer = setInterval(() => { try { writer.flush(); } catch (_) {} }, 500);

    let vTap = null, aTap = null;
    const sink = {
        /** The engine's stream: {container, video:{mux,width,height,fps}, audio:{mux,sampleRate,channels}|null}. */
        begin(s) {
            shape = s;
            vTap = videoTap({
                mux: s.video.mux, frameUs: Math.round(1e6 / (s.video.fps || 30)),
                onConfig: (c) => { if (!cfg.video) cfg.video = { codec: s.video.mux, codecString: c.codecString, width: s.video.width, height: s.video.height, fps: s.video.fps || 30, description: c.description }; },
                onRecord: (r) => { if (!closed) writer.record(r); },
            });
            if (s.audio) {
                aTap = audioTap({
                    onConfig: (c) => { cfg.audio = { codec: s.audio.mux === 'aac' ? 'aac' : 'opus', codecString: c.codecString, sampleRate: c.sampleRate || s.audio.sampleRate, channels: c.channels || s.audio.channels, description: c.description }; },
                    onRecord: (r) => { if (!closed) writer.record(r); },
                });
            }
        },
        video(chunk, meta, ts) { if (vTap) { try { vTap(chunk, meta, ts); } catch (e) { tel('record_replay_tap_error', { k: 'v', err: String(e && e.message || e).slice(0, 80) }); } } },
        audio(chunk, meta, ts) { if (aTap) { try { aTap(chunk, meta, ts); } catch (e) { tel('record_replay_tap_error', { k: 'a', err: String(e && e.message || e).slice(0, 80) }); } } },
        end() { try { writer.flush(); } catch (_) {} },
    };

    function tel(f, p) { try { if (window.__ae) window.__ae('feature_use', Object.assign({ slug: 'screen-recorder', f }, p || {})); } catch (_) {} }

    return {
        sink,
        windowS,
        storeKind: st.kind,
        failure: () => failure,
        haveUs: () => (writer ? writer.ix.durationUs() : 0),
        /**
         * The last `seconds` as one file. The clip begins on the keyframe at or
         * before that moment (≤ 1 s earlier: the engine keys every second in this
         * lane) and ends on the newest frame the loop holds.
         */
        async save(seconds) {
            if (!cfg.video || !shape) { const e = new Error('replay: nothing recorded yet'); e.code = 'replay_empty'; throw e; }
            writer.flush();
            const end = writer.ix.lastVideoEnd;
            const range = [end - seconds * 1e6, end];
            building.add(range);
            const ext = shape.container === 'webm' ? 'webm' : 'mp4';
            const out = 'r' + stamp + own + '-clip' + (++clipN) + '.' + ext;
            files.add(out);
            try {
                const win = writer.ix.window(range[0], range[1]);
                if (!win) { const e = new Error('replay: nothing recorded yet'); e.code = 'replay_empty'; throw e; }
                // the disk refused the clip once — say so before building a second one
                if (st.kind === 'disk') { const r = await roomFor(estimateBytes(win)); if (!r.ok) { const e = new Error('replay: no room for the clip'); e.code = 'replay_no_room'; throw e; } }
                // the flush above went to the worker: reads queue behind it
                const build = ext === 'webm' ? buildWebm : buildMp4;
                const Muxer = ext === 'webm' ? window.WebMMuxer : window.Mp4Muxer;
                const c = { video: cfg.video, audio: cfg.audio };
                const res = await build({ store: st, win, cfg: c, Muxer, outName: out });
                const blob = await st.getFile(out, ext === 'webm' ? 'video/webm' : 'video/mp4');
                clips.push(out);
                // A clip's file lives in the site's storage while a download may
                // read it; the page keeps the last KEEP_CLIPS for «download
                // again», older ones go (an evening of clips must not fill the
                // disk the loop needs).
                while (clips.length > KEEP_CLIPS) { const old = clips.shift(); try { await st.remove(old); } catch (_) {} files.delete(old); }
                return { blob, ext, durUs: res.durationUs, bytes: res.bytes, file: out };
            } finally {
                building.delete(range);
            }
        },
        /** A clip whose copy is somewhere else now (the person's folder): its file goes. */
        async discard(file) {
            const i = clips.indexOf(file);
            if (i < 0) return;
            clips.splice(i, 1);
            try { await st.remove(file); } catch (_) {}
            files.delete(file);
        },
        async close() {
            if (closed) return;
            closed = true;
            clearInterval(flushTimer);
            try { writer.close(); } catch (_) {}
            // the segments go at once; the clips stay while the page lives (a
            // download may still be reading one) — the janitor takes them after
            const segs = [...files].filter((f) => f.endsWith('.bin'));
            for (const f of segs) { try { await st.remove(f); } catch (_) {} files.delete(f); }
        },
    };
}

window.SrReplay = {
    /** WebCodecs + a private file system: the loop's preconditions. */
    supported() {
        try {
            return typeof VideoEncoder === 'function' && typeof MediaStreamTrackProcessor === 'function'
                && !!(navigator.storage && typeof navigator.storage.getDirectory === 'function');
        } catch (_) { return false; }
    },
    open,
    WINDOWS_S: [30, 60, 120, 300],
};
try { window.dispatchEvent(new Event('sr-replay-ready')); } catch (_) {}
